<template>
  <PageContent>
    <div v-if="product && (parseInt(packageUnit, 10) || 0) > -1">
      <ProductHeaderInfoNonRx
        :back-url="backUrl"
        :product="product"
        :selected-variant="selectedVariant"
      />
      <ProductDetailThreeColumns
        :product="product"
        :get-attribute="getProductAttribute(product, selectedVariant)"
        :product-detail-properties="PRODUCT_DETAIL_ATTRIBUTE_KEYS_CF"
        :selected-variant="selectedVariant"
        :detail-name-mapping="detailNameMapping"
      >
        <template #actions>
          <div class="space-y-2 bg-white text-sm">
            <ProductPromotionGiftConditionBanner
              v-if="!areGiftConditionsMet && isGiftAddedToBasket"
              :product="product"
            />
            <ProductPromotionBanners v-else :product="product" />
            <div
              v-if="
                packageUnits.length > 0 &&
                cheapestVariant?.attributes?.deliveryContent
              "
            >
              <div class="mb-2 text-xl font-medium">
                {{ $t('pdp.package_type') }}
              </div>
              <div class="mb-8">
                <div
                  class="top-row"
                  :class="{
                    'size-selection-row without-discount':
                      packageUnits.length <= 1,
                    'size-selection-row with-discount': packageUnits.length > 1,
                  }"
                >
                  <BaseText> {{ $t('pdp.size_heading') }} </BaseText>
                  <BaseText> {{ $t('pdp.price_per_litre') }} </BaseText>
                  <BaseText v-if="packageUnits.length > 1">
                    <span>{{ $t('pdp.saving') }}*</span>
                  </BaseText>
                  <BaseText>{{ $t('pdp.price_heading') }}</BaseText>
                </div>
                <div
                  v-for="(unit, idx) in packageUnits"
                  :key="idx"
                  class="relative items-center text-[15px]"
                  :class="{
                    'size-selection-row without-discount':
                      packageUnits.length <= 1,
                    'size-selection-row with-discount': packageUnits.length > 1,
                    'text-neutral-30': unit.isSoldOut,
                  }"
                >
                  <FimRadio
                    v-if="packageUnits.length > 1"
                    v-model="packageUnit"
                    class="text-[15px] md:text-base"
                    :radio-value="idx"
                    radio-name="package-units"
                    :disabled="unit.isSoldOut"
                    :label="unit.label"
                    @update:model-value="onSwitchVariant"
                  />
                  <div v-else class="text-[15px] md:text-base">
                    {{ unit.label }}
                  </div>
                  <div class="pt-0.5 text-[15px] md:text-base">
                    <span v-if="unit.pricePerUnit">
                      {{ toCurrency(unit.pricePerUnit) }}
                    </span>
                    <span v-else> – </span>
                  </div>
                  <div
                    v-if="packageUnits.length > 1"
                    class="w-auto font-semibold"
                  >
                    <span
                      v-if="unit.discount"
                      class="w-auto whitespace-nowrap rounded-full bg-red-500 px-2 py-0.5 text-[14px] font-medium tracking-wide text-white"
                    >
                      -
                      {{ unit.discount }}
                      %
                    </span>
                  </div>
                  <ProductPrice
                    class="text-[15px]"
                    :price="unit.bapiPrice"
                    :quantity="1"
                    :applied-reductions="unit.bapiPrice.appliedReductions"
                    :show-price-from="hasSpecial"
                    :show-price-reduction-badge="hasSpecial"
                    :align="'right'"
                    :break-line="true"
                  />
                  <span
                    v-if="unit.lowestPrice"
                    class="absolute bottom-1 right-0 text-xs text-gray-600"
                  >
                    {{ $t('pdp.lowest_price') }}
                    {{ `${unit.lowestPrice}` }}
                  </span>
                </div>
                <div
                  v-if="packageUnits.length > 1"
                  class="mt-5 text-gray-600 md:mt-3"
                >
                  * {{ $t('pdp.package_discount_info') }}
                </div>
              </div>

              <div>
                <div>
                  <div class="mb-2 w-full text-sm font-medium">
                    {{ $t('pdp.packages_heading') }}
                  </div>
                  <div class="mb-3 flex space-x-2">
                    <QuantityPicker
                      :value="quantity"
                      :max-quantity="
                        variantsSortedByPrice[parseInt(packageUnit, 10) || 0]
                          .stock.quantity
                      "
                      @input="updateQuantity($event)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-9">
              <div class="mb-2 text-xl font-medium">
                {{ contentDescription }}
              </div>
            </div>
            <ProductSubscription
              v-if="
                isSubscriptionEnabled && isProductSubscriptionEligible(product)
              "
              :product="product"
              :quantity="quantity"
              :variants="[selectedVariant]"
              class="mt-4"
              @add-item-to-basket="addItemToBasket($event)"
            >
              <template #price="slotProps">
                <ProductPrice
                  :price="slotProps.price || price"
                  size="md"
                  :quantity="quantity"
                  :applied-reductions="
                    slotProps.price
                      ? slotProps.price?.appliedReductions
                      : price.appliedReductions
                  "
                  :show-price-from="hasSpecial"
                  :show-price-reduction-badge="hasSpecial"
                  :show-discount="true"
                />

                <span v-if="lowestPrice" class="flex text-sm text-gray-600">
                  {{ $t('pdp.lowest_price') }}
                  {{ `${lowestPrice}` }}
                </span>
              </template>
              <template #tax-extras>
                {{ $t('pdp.with_tax') }}
                <ProductShippingCosts
                  :price="price"
                  :quantity="quantity"
                  :product-id="product.id"
                /><br />
                {{ $t('pdp.delivery_location_information') }}
              </template>
              <template #delivery-estimates>
                <ProductDetailDeliveryEstimates
                  :key="deliveryEstimatesKey"
                  :stock="stock"
                  :sales-status="salesStatus"
                  :product="product"
                  class="mt-3"
                />
              </template>
              <template #recommendations>
                <FimRecommendationStack
                  v-if="productRecommendations"
                  :recommendation-product="product"
                  :product-recommendations="productRecommendations"
                  class="mb-6"
                  :current-product-category="currentProductCategory"
                  location="PDP"
                />
              </template>
            </ProductSubscription>
            <div v-else class="mt-9">
              <ProductPrice
                class="mb-4"
                :price="price"
                size="2xl"
                :quantity="quantity"
                :applied-reductions="price.appliedReductions"
                :show-price-from="hasSpecial"
                :show-price-reduction-badge="hasSpecial"
                :show-discount="true"
              />
              <span class="text-sm text-gray-600">
                {{ $t('pdp.with_tax') }}
                <ProductShippingCosts
                  :price="price"
                  :quantity="quantity"
                  :product-id="product.id"
                /><br />
                {{ $t('pdp.delivery_location_information') }}
              </span>
              <span v-if="lowestPrice" class="flex text-sm text-gray-600">
                {{ $t('pdp.lowest_price') }}
                {{ `${lowestPrice}` }}
              </span>
              <ProductDetailDeliveryEstimates
                :key="deliveryEstimatesKey"
                :stock="stock"
                :sales-status="salesStatus"
                :product="product"
                class="mt-3"
              />
              <div class="my-6">
                <div class="flex h-10 items-center space-x-2">
                  <FimButton
                    type="primary"
                    data-testid="add-to-cart"
                    class="w-full"
                    :disabled="
                      !isProductAvailable(product) ||
                      !isVariantAvailable(product, selectedVariant)
                    "
                    :loading="isAddingToCart"
                    @click="() => addItemToBasket()"
                  >
                    {{ $t('pdp.add_label') }}
                    <template #iconAfter>
                      <LazyIconFielmannCart
                        class="basket-ic-color mr-2 size-6"
                      />
                    </template>
                  </FimButton>
                  <WishlistToggle
                    :product="product"
                    :variant="selectedVariant"
                    layout="button"
                  />
                </div>
                <FadeInTransition>
                  <ProductPromotionGifts
                    v-if="isBuyXGetYPrioritized && !isGiftAddedToBasket"
                    :product="product"
                    class="mt-6"
                  />
                </FadeInTransition>
                <ProductShippingCostInformation class="max-w-2xs" />
              </div>
              <FimRecommendationStack
                v-if="productRecommendations"
                :recommendation-product="product"
                :product-recommendations="productRecommendations"
                :current-product-category="currentProductCategory"
                location="PDP"
                class="mb-6"
              />
            </div>
            <BenefitsList slug="care" />
          </div>
        </template>
        <template #manuals>
          <div v-if="manualUrl || complianceUrl">
            <Headline class="mb-6" tag="span" look="h3">
              {{ $t('pdp.manual.headline') }}
            </Headline>
            <ProductDetailManualLink
              v-if="manualUrl"
              class="mb-6"
              :url="manualUrl"
              translation-key="pdp.manual.copyCleaningFluid"
            />
            <ProductDetailManualLink
              v-if="complianceUrl"
              :url="complianceUrl"
              translation-key="pdp.manual.copyCompliance"
              link-translation-key="pdp.manual.linkCompliance"
            />
          </div>
        </template>
      </ProductDetailThreeColumns>
      <FimRecommendations
        v-if="recommendationRefXsell"
        :recommendation-product="product"
        :product-recommendations="recommendationRefXsell"
        :title="getRecommendationTitle('recommendationRefXsell', $i18n)"
        :current-product-category="currentProductCategory"
        class="mt-12"
        location="PDP"
      >
        <FimRecommendationLinks field-name="care_products_links" />
      </FimRecommendations>
      <BazaarVoice :id="product.id" show="reviews" />
      <ClientOnly>
        <ProductDetailCartOverlay
          :recommendation-product="product"
          :product-recommendations="productRecommendations"
          :recommendation-attribute="
            PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_RELATED
          "
          :current-product-category="currentProductCategory"
        >
          <FimRecommendationLinks field-name="care_products_links" />
        </ProductDetailCartOverlay>
      </ClientOnly>
    </div>
  </PageContent>
</template>

<script setup lang="ts">
/* eslint sonarjs/cognitive-complexity: 1 */

import {
  getFirstAttributeValue,
  type AddOrUpdateItemType,
  type BasketItemDisplayData,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'
import { Action } from '~/plugins/toast'
import { isEmpty } from 'radash'
import { DisplayDataAttributes } from '~/composables/pdp/pdpTypes'
import useUiState from '~/composables/ui/useUiState'
import { useCareProducts } from '~/composables/useCareProducts'
import { useFimBasket } from '~/composables/useFimBasket'
import { isProductSubscriptionEligible } from '~/modules/subscription/helpers/subscription'
import {
  PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_RELATED,
  PRODUCT_DETAIL_ATTRIBUTE_KEYS_CF,
} from '~/constants/product'
import { PRODUCT_WITH_CARE } from '~/constants/withParams'
import { getFirstAttributeLabel } from '~/utils/attribute'
import { getLowestPriorPrice, toCurrency } from '~/utils/price'
import {
  ProductCategories,
  getProductAttribute,
  getRecommendationTitle,
  isProductAvailable,
  isVariantAvailable,
} from '~/utils/product'
import { getQueryParam } from '~/utils/route'
import {
  generateBreadcrumbSchema,
  getBreadcrumbSchemaItems,
  getProductHead,
  generateProductSchema,
} from '~/utils/seo'

const props = defineProps({
  baseProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
})
const context = useNuxtApp()
const { $currentShop, $fimLocalePath, $alert, $i18n, $featureToggle } = context
const { setPageState } = usePageState()
const route = useRoute()
const router = useRouter()
const scope = effectScope()
const { trackPageView } = useTrackingEvents()
onScopeDispose(() => scope.stop())
const { getProductDetailRoute } = useRouteHelpers()
const queryVariantId = computed(() => getQueryParam(route.query, 'variantid'))
const { isSubscriptionEnabled } = $featureToggle

const { data: shopCustomData } = useCustomData()

onMounted(() => {
  // if user navigates from PLP to out of stock variant, redirect to first in stock variant
  if (
    (!queryVariantId.value ||
      parseInt(queryVariantId.value, 10) === cheapestVariant.value?.id) &&
    !isVariantAvailable(product.value, cheapestVariant.value)
  ) {
    const cheapestVariantAvailable = variantsSortedByPrice.value.find((v) =>
      isVariantAvailable(product.value, v),
    )

    if (cheapestVariantAvailable) {
      router.replace(
        $fimLocalePath(
          getProductDetailRoute(product.value, {
            ...route.query,
            variantid: cheapestVariantAvailable.id,
          }),
        ),
      )

      productPromise.then(() => setVariantId(cheapestVariantAvailable.id))
    }
  }
})

const productPromise = useProductDetails(props.baseProduct, {
  params: {
    with: PRODUCT_WITH_CARE,
  },
  options: { immediate: true },
})

const {
  backUrl,
  breadcrumbs,
  deliveryForecast,
  hasSpecial,
  inStock,
  stock,
  isSellableWithoutStock,
  packageUnit,
  manualUrl,
  complianceUrl,
  price,
  product: detailedProduct,
  trackProductView,
  quantity,
  selectedVariant,
  variantsSortedByPrice,
  salesStatus,
  currentProductCategory,
  setVariantId,
} = await productPromise

const product = computed(() => detailedProduct.value || props.baseProduct)

const {
  isBuyXGetYPrioritized,
  isGiftAddedToBasket,
  areGiftConditionsMet,
  highestPriorityPromotion,
} = useProductPromotions(product)

// Product recommendations
const { data: productRecommendations } = useAsyncProductRecommendations(
  [...PRODUCT_RECOMMENDATION_ATTRIBUTES],
  product.value,
)

const recommendationRefXsell = computed(
  () => productRecommendations.value?.recommendationRefXsell,
)

const head = computed(() =>
  getProductHead(product.value, selectedVariant.value, price.value, context),
)

useHead(() => head.value)
onMounted(() => {
  trackProductView(product.value)

  trackPageView({
    title: head.value.title,
    pageType: 'product',
  })
})

const { trackAddToBasket } = useTrackingEvents()

const { packageUnits, cheapestVariant } = useCareProducts(product)

const { addItem, items: basketItems } = useFimBasket()
const isAddingToCart = ref(false)

const contentDescription = computed(() => {
  if (!selectedVariant.value) {
    return ''
  }

  return getFirstAttributeLabel(
    selectedVariant.value?.attributes,
    'deliveryContent',
  )
})

const selectedVariantId = computed(() =>
  selectedVariant.value ? [selectedVariant.value.id] : [],
)

const { openBasketOverlay } = useUiState()

const updateQuantity = (value: number) => {
  quantity.value = value
}

const isConsultantUsed = computed(
  () => getQueryParam(route.query, 'consultant', '0') === '1',
)

const addItemToBasket = async (
  basketItemSubscriptionAttributes?: MaybeRefOrGetter<BasketItemSubscriptionAttributes>,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  if (
    !isAddingToCart.value &&
    variantsSortedByPrice.value &&
    variantsSortedByPrice.value.length
  ) {
    isAddingToCart.value = true
    const variant =
      variantsSortedByPrice.value?.[parseInt(packageUnit.value, 10) || 0]
    const size = getPackagingType(variant)
    const eyeCareType = getEyeCareType()
    const subscriptionAttributes = toValue(basketItemSubscriptionAttributes)

    const displayDataPayload: { displayData: BasketItemDisplayData } = {
      displayData: {},
    }
    if (
      variant.stock?.quantity === 0 &&
      variant.stock?.isSellableWithoutStock
    ) {
      displayDataPayload.displayData[
        DisplayDataAttributes.deliveryForecastAttribute
      ] = {
        key: $i18n.t('basket.displayData.deliveryForecastFallback.key'),
        label: $i18n.t('basket.displayData.deliveryForecastFallback.label'),
        value:
          shopCustomData.value?.deliveryEstimatesProductType
            ?.sellableWithoutStockAdditionalTime ??
          $i18n.t('basket.displayData.deliveryForecastFallback.value'),
      }
    }

    if (eyeCareType?.value && eyeCareType?.label) {
      displayDataPayload.displayData['attribute-1'] = {
        key: eyeCareType.key ?? '',
        label: eyeCareType.label ?? '',
        value: eyeCareType.value ?? '',
      }
    }

    if (size?.key && size?.value && size?.label) {
      displayDataPayload.displayData[DisplayDataAttributes.sizeAttribute] = {
        key: size.key,
        label: size.label,
        value: size.value,
      }
    }

    if (
      isSubscriptionEnabled.value &&
      isSubscriptionAlreadyInBasket(
        !!subscriptionAttributes,
        variant.id,
        basketItems.value,
      )
    ) {
      $alert.show(
        $i18n.t('basket.notification.subscription_already_in_basket_error', {
          productName: getProductName(product.value),
        }),
        Action.confirm,
      )
      return
    }

    const promotionId = highestPriorityPromotion.value?.id
    const branchId = getBranchIdFromSession()

    const customData = {
      ...(branchId && { branchId }),
      ...(isConsultantUsed.value && { consultantUsed: true }),
      designerOrder: isDesignerProduct(product),
    } as FimBasketItemCustomData

    const addItemPayload: AddOrUpdateItemType = {
      variantId: variant?.id,
      quantity: Number(quantity.value),
      ...(isEmpty(displayDataPayload) ? {} : displayDataPayload),
      ...(isEmpty(customData) ? {} : { customData }),
      ...(!promotionId && { promotionId: null }),
      ...(promotionId && !isBuyXGetYPrioritized.value && { promotionId }),
    }

    if (variant) {
      if (subscriptionAttributes?.customData) {
        addItemPayload.customData = {
          ...addItemPayload.customData,
          ...subscriptionAttributes.customData,
        }
      }
      if (subscriptionAttributes?.displayData) {
        addItemPayload.displayData = {
          ...addItemPayload.displayData,
          ...subscriptionAttributes.displayData,
        }
      }

      await addItem(addItemPayload as any)

      trackAddToBasket(product.value, Number(quantity.value), variant, {
        customData: addItemPayload.customData,
      })
    }

    isAddingToCart.value = false
    openBasketOverlay()
  }
}

const getPackagingType = (variant: Variant) => {
  const attribute = getFirstAttributeValue(
    variant?.attributes,
    'packingTypeDescription',
  )

  return {
    key: variant?.attributes?.packingTypeDescription?.key,
    label: $i18n.t('packagingSize'),
    value: attribute?.label,
  }
}

const getEyeCareType = () => {
  const attribute = getFirstAttributeValue(
    product?.value?.attributes,
    'eyeCareProductType',
  )

  return {
    key: product?.value?.attributes?.eyeCareProductType?.key,
    label: product?.value?.attributes?.eyeCareProductType?.label,
    value: attribute?.label,
  }
}

const detailNameMapping = {
  deliveryContent: $i18n.t('pdp.details.deliveryContent'),
}

const lowestPrice = computed(() =>
  getLowestPriorPrice(
    selectedVariant.value?.price,
    selectedVariant.value?.lowestPriorPrice,
    Number(quantity.value),
  ),
)

const breadcrumbSchema = computed(() =>
  getBreadcrumbSchemaItems(
    breadcrumbs.value,
    $currentShop.domain ?? '',
    route.fullPath,
    head.value.title || '',
  ),
)

// todo: cleanup useProductDetails
const onSwitchVariant = (index: string | undefined) => {
  const unit = packageUnits.value?.[Number(index)]

  if (!product.value || !unit || unit.isSoldOut) {
    return
  }

  setVariantId(unit.variantId)

  const newUrl = $fimLocalePath(
    getProductDetailRoute(product.value, {
      ...route.query,
      variantid: unit.variantId,
    }),
  )
  window.history.pushState({}, '', newUrl)
}

const deliveryEstimatesKey = ref(0)
scope.run(() => {
  if (import.meta.server) {
    return
  }

  watch(
    queryVariantId,
    (next) => {
      productPromise.then(() => setVariantId(next))
    },
    { immediate: true },
  )

  // todo: this was a hack to get around UI not updating if I remember correctly. Check if still needed
  watch([inStock, isSellableWithoutStock], () => {
    deliveryEstimatesKey.value += 1
  })
})

setPageState('pageType', 'product')
setPageState('productCategory', ProductCategories.careproducts)

const productSchema = computed(() =>
  generateProductSchema(
    product.value,
    selectedVariant.value,
    price.value,
    context,
  ),
)

useJsonld(() => productSchema.value)
useJsonld(() => generateBreadcrumbSchema(breadcrumbSchema.value))
</script>
